import { type BrandId } from "../brands";
import { getMobileAppStoreUrls, type MobileAppStoreUrls } from "../mobile-apps";
import { getDeviceType } from "../url";
import {
  DESTINATION_PARAM,
  HANDOFF_TOKEN_PARAM,
  LEAD_ID_PARAM,
  SNOWPLOW_SESSION_ID_PARAM,
  SURFACE_ELEMENT_PARAM,
  SURFACE_PAGE_PARAM,
} from "./referrer-params";

export type MobileAppDownloadPlatform = "android" | "ios";

type DeviceType = ReturnType<typeof getDeviceType>;

export type InstallAcquisition = {
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  utm_id?: string | null;
  gclid?: string | null;
  gbraid?: string | null;
  gad_source?: string | null;
  fbclid?: string | null;
  ttclid?: string | null;
  msclkid?: string | null;
  oppref?: string | null;
  obref?: string | null;
};

export type InstallHandoff = {
  token: string;
  destination?: string | null;
};

export type InstallSurface = {
  page?: string | null;
  element?: string | null;
};

type MobileAppDownloadUrlConfig = {
  brandId: BrandId;
  acquisition?: InstallAcquisition;
  surface?: InstallSurface;
  snowplowSessionId?: string | null;
  leadId?: string | null;
  handoff?: InstallHandoff | null;
  playStoreBaseUrl?: string;
  appStoreTrackingUrl?: string;
  platform?: string | null;
  deviceType?: DeviceType;
};

type MobileAppStoreUrlConfig = {
  brandId: BrandId;
  platform: MobileAppDownloadPlatform;
  playStoreBaseUrl?: string;
  appStoreTrackingUrl?: string;
};

const isDownloadPlatform = (
  platform: string | null | undefined,
): platform is MobileAppDownloadPlatform =>
  platform === "android" || platform === "ios";

const getStoreUrls = ({
  brandId,
  playStoreBaseUrl,
  appStoreTrackingUrl,
}: Pick<
  MobileAppDownloadUrlConfig,
  "brandId" | "playStoreBaseUrl" | "appStoreTrackingUrl"
>): MobileAppStoreUrls => ({
  ...getMobileAppStoreUrls(brandId),
  ...(playStoreBaseUrl && { android: playStoreBaseUrl }),
  ...(appStoreTrackingUrl && { ios: appStoreTrackingUrl }),
});

const appendPresent = (
  params: URLSearchParams,
  entries: Record<string, string | null | undefined>,
): void => {
  for (const [key, value] of Object.entries(entries)) {
    if (value) {
      params.set(key, value);
    }
  }
};

const appendSurface = (
  params: URLSearchParams,
  surface: InstallSurface,
): void => {
  if (surface.page) {
    params.set(SURFACE_PAGE_PARAM, surface.page);
  }
  if (surface.element) {
    params.set(SURFACE_ELEMENT_PARAM, surface.element);
  }
};

const buildPlayStoreUrl = (
  baseUrl: string,
  acquisition: InstallAcquisition,
  surface: InstallSurface,
  snowplowSessionId: string | null | undefined,
  leadId: string | null | undefined,
  handoff: InstallHandoff | null | undefined,
): string => {
  const url = new URL(baseUrl);
  const referrer = new URLSearchParams();
  appendPresent(referrer, acquisition);
  appendSurface(referrer, surface);
  appendPresent(referrer, {
    [SNOWPLOW_SESSION_ID_PARAM]: snowplowSessionId,
    [LEAD_ID_PARAM]: leadId,
    [HANDOFF_TOKEN_PARAM]: handoff?.token,
    [DESTINATION_PARAM]: handoff?.destination,
  });
  url.searchParams.set("referrer", referrer.toString());
  return url.toString();
};

const buildAppStoreUrl = (
  appStoreTrackingUrl: string,
  acquisition: InstallAcquisition,
  surface: InstallSurface,
): string => {
  const url = new URL(appStoreTrackingUrl);
  appendPresent(url.searchParams, acquisition);
  appendSurface(url.searchParams, surface);
  return url.toString();
};

export const getMobileAppStoreUrl = ({
  brandId,
  platform,
  playStoreBaseUrl,
  appStoreTrackingUrl,
}: MobileAppStoreUrlConfig): string | null => {
  const storeUrls = getStoreUrls({
    brandId,
    playStoreBaseUrl,
    appStoreTrackingUrl,
  });

  return storeUrls[platform] ?? null;
};

export const getMobileAppDownloadUrl = (
  config: MobileAppDownloadUrlConfig,
): string | null => {
  const requestedPlatform =
    config.platform ?? config.deviceType ?? getDeviceType();

  if (!isDownloadPlatform(requestedPlatform)) {
    return null;
  }

  const storeUrl = getMobileAppStoreUrl({
    brandId: config.brandId,
    platform: requestedPlatform,
    playStoreBaseUrl: config.playStoreBaseUrl,
    appStoreTrackingUrl: config.appStoreTrackingUrl,
  });

  if (!storeUrl) {
    return null;
  }

  const acquisition = config.acquisition ?? {};
  const surface = config.surface ?? {};

  if (requestedPlatform === "android") {
    return buildPlayStoreUrl(
      storeUrl,
      acquisition,
      surface,
      config.snowplowSessionId,
      config.leadId,
      config.handoff,
    );
  }

  return config.appStoreTrackingUrl
    ? buildAppStoreUrl(storeUrl, acquisition, surface)
    : storeUrl;
};

export type InstallUrlParams = {
  brandId: BrandId;
  platform: MobileAppDownloadPlatform;
  surface: InstallSurface;
  acquisition?: InstallAcquisition;
};

export const getInstallUrl = ({
  brandId,
  platform,
  surface,
  acquisition,
}: InstallUrlParams): string | null => {
  if (platform === "ios") {
    return getMobileAppStoreUrl({ brandId, platform });
  }

  return getMobileAppDownloadUrl({
    brandId,
    platform,
    surface,
    acquisition,
  });
};
